/**
 * FarmRoadHistoricalViewModal
 *
 * Modal full-screen pour afficher une analyse historique
 * Réutilise l'interface EXACTE de FarmRoadInsightsPanel pour une expérience cohérente
 */

import ReactMarkdown from 'react-markdown';
import {
  Dialog,
  DialogContent,
  Button,
  Card,
  CardContent,
  Typography
} from '@mui/material';
import {
  X,
  Calendar,
  Clock,
  MapPin,
  Leaf,
  History,
  Sun,
  Thermometer,
  Droplets,
  Wind,
  Download
} from 'lucide-react';
import { pdf } from '@react-pdf/renderer';
import FarmRoadPDFReport from './FarmRoadPDFReport';

const FarmRoadHistoricalViewModal = ({ open, onClose, insight }) => {
  if (!insight) return null;

  // Debug: Voir l'objet insight complet
  console.log('[HistoricalView] Insight complet reçu:', insight);
  console.log('[HistoricalView] thresholds_evaluation:', insight.thresholds_evaluation);

  /**
   * Formatte une date
   */
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  /**
   * Nettoie le texte markdown pour assurer un bon formatage
   * - Ajoute des line breaks après les titres ##
   * - Nettoie les line breaks multiples
   */
  const cleanMarkdownText = (text) => {
    if (!text || typeof text !== 'string') return text;

    return text
      // Assurer un saut de ligne après les titres ## (si pas déjà présent)
      .replace(/(##\s+[^\n]+)([^\n])/g, '$1\n\n$2')
      // Assurer un saut de ligne avant les titres ## (si pas déjà présent)
      .replace(/([^\n])(##\s+)/g, '$1\n\n$2')
      // Nettoyer les multiples line breaks (3+ → 2)
      .replace(/\n{3,}/g, '\n\n')
      // Trim les espaces
      .trim();
  };

  /**
   * Transforme indicators_snapshot (flat) vers structure nested attendue par PDF
   * Flat: {dli: {value, unit}, vpd_diurnal: {value, unit}}
   * Nested: {light: {dli: value}, vpd: {diurnalMean: value}}
   */
  const transformIndicatorsForPDF = (snapshot) => {
    if (!snapshot) return {};

    return {
      light: {
        dli: snapshot.dli?.value,
        sunlightDuration: snapshot.sunlight_duration?.value,
        meanDiurnalRadiation: snapshot.radiation_mean_diurnal?.value
      },
      vpd: {
        diurnalMean: snapshot.vpd_diurnal?.value,
        mean24h: snapshot.vpd_mean?.value
      },
      temperature: {
        mean: snapshot.temperature_mean?.value,
        diurnalMean: snapshot.temperature_diurnal?.value,
        nocturnalMean: snapshot.temperature_nocturnal?.value,
        amplitude: snapshot.temperature_amplitude?.value,
        heatStressHours: snapshot.temperature_heat_stress?.value,
        coldStressHours: snapshot.temperature_cold_stress?.value
      },
      co2: {
        diurnalMean: snapshot.co2_diurnal?.value,
        saturationHours: snapshot.co2_saturation?.value
      },
      humidity: {
        mean24h: snapshot.soil_moisture?.value
      },
      airHumidity: {
        mean24h: snapshot.humidity?.value
      }
    };
  };

  /**
   * Calcule la durée entre deux dates au format "X jour(s) Y heure(s)"
   */
  const calculateDuration = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const durationMs = end - start;

    const totalHours = Math.floor(durationMs / (1000 * 60 * 60));
    const days = Math.floor(totalHours / 24);
    const hours = totalHours % 24;

    if (days === 0) {
      return `${hours} heure${hours > 1 ? 's' : ''}`;
    } else if (hours === 0) {
      return `${days} jour${days > 1 ? 's' : ''}`;
    } else {
      return `${days} jour${days > 1 ? 's' : ''} ${hours} heure${hours > 1 ? 's' : ''}`;
    }
  };

  /**
   * Génère et télécharge le PDF
   */
  const handleDownloadPDF = async () => {
    try {
      // Transformer la structure flat vers nested
      const nestedIndicators = transformIndicatorsForPDF(insight.indicators_snapshot);

      // Préparer les données dans le format attendu par FarmRoadPDFReport
      const analysisResult = {
        success: true,
        metadata: {
          farmName: insight.farm_name || 'Ferme',
          sectorName: insight.sector_name || 'Toute la ferme',
          culture: insight.culture || 'framboises',
          period: {
            start: insight.start_date,
            end: insight.end_date,
            duration: calculateDuration(insight.start_date, insight.end_date)
          },
          analysisPeriod: insight.analysis_period
        },
        data: {
          dailyIndicators: nestedIndicators,
          dailyEvaluation: {
            byIndicator: insight.thresholds_evaluation || {}
          },
          thresholdsEvaluation: {
            byIndicator: insight.thresholds_evaluation || {}
          }
        },
        analysis: {
          parsed: {
            sections: {
              diagnostic: insight.diagnostic || '',
              detailedAnalysis: insight.detailed_analysis || '',
              recommendations: insight.recommendations || '',
              predictions: insight.predictions || ''
            }
          }
        },
        measurementsCount: insight.measurements_count || 0,
        scientificReferences: insight.scientific_references || ''
      };

      console.log('[PDF] Données préparées:', analysisResult);

      const blob = await pdf(<FarmRoadPDFReport analysisResult={analysisResult} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Analyse_${insight.insight_id}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('[PDF] Erreur génération:', error);
    }
  };

  /**
   * Rendu de la bannière d'en-tête
   */
  const renderHeader = () => (
    <div className="bg-blue-50 border-b border-blue-200 p-4 mb-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-blue-100 p-2 rounded-lg">
            <History className="h-5 w-5 text-blue-700" />
          </div>
          <div>
            <h3 className="font-semibold text-blue-900">Analyse Historique</h3>
            <p className="text-sm text-blue-700">
              {formatDate(insight.created_at)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handleDownloadPDF}
            startIcon={<Download className="h-4 w-4" />}
            variant="outlined"
            size="small"
            sx={{ borderColor: '#3b82f6', color: '#3b82f6' }}
          >
            Télécharger PDF
          </Button>
          <Button onClick={onClose} sx={{ minWidth: 'auto' }}>
            <X className="h-5 w-5" />
          </Button>
        </div>
      </div>

      {/* Infos contextuelles */}
      <div className="grid grid-cols-4 gap-3 mt-4">
        <div className="bg-white rounded p-2">
          <div className="flex items-center gap-1 mb-1">
            <MapPin className="h-3 w-3 text-gray-600" />
            <span className="text-xs text-gray-600">Secteur</span>
          </div>
          <p className="text-sm font-semibold text-gray-900">
            {insight.sector_name || 'Toute la ferme'}
          </p>
        </div>

        <div className="bg-white rounded p-2">
          <div className="flex items-center gap-1 mb-1">
            <Leaf className="h-3 w-3 text-green-600" />
            <span className="text-xs text-gray-600">Culture</span>
          </div>
          <p className="text-sm font-semibold text-gray-900">
            {insight.culture || 'Framboises'}
          </p>
        </div>

        <div className="bg-white rounded p-2">
          <div className="flex items-center gap-1 mb-1">
            <Calendar className="h-3 w-3 text-gray-600" />
            <span className="text-xs text-gray-600">Période</span>
          </div>
          <p className="text-sm font-semibold text-gray-900">
            {formatDate(insight.start_date)}
          </p>
          <p className="text-xs text-gray-500">
            → {formatDate(insight.end_date)}
          </p>
        </div>

        <div className="bg-white rounded p-2">
          <div className="flex items-center gap-1 mb-1">
            <Clock className="h-3 w-3 text-gray-600" />
            <span className="text-xs text-gray-600">Mesures</span>
          </div>
          <p className="text-sm font-semibold text-gray-900">
            {insight.measurements_count || 0}
          </p>
        </div>
      </div>
    </div>
  );

  /**
   * Rendu de la section indicateurs 24h
   * UTILISE LA MÊME LOGIQUE QUE FarmRoadInsightsPanel.renderDaily24hSection()
   */
  const render24hIndicatorsSection = () => {
    console.log('[Indicators] insight.thresholds_evaluation:', insight.thresholds_evaluation);
    console.log('[Indicators] Type:', typeof insight.thresholds_evaluation);

    if (!insight.thresholds_evaluation || typeof insight.thresholds_evaluation !== 'object') {
      console.log('[Indicators] Pas de thresholds_evaluation, retour null');
      return null;
    }

    const getConfig = (sevRaw) => {
      const sev = ['critical', 'warning', 'optimal'].includes(sevRaw) ? sevRaw : 'acceptable';
      return {
        color: sev === 'critical' ? '#dc2626' : sev === 'warning' ? '#f59e0b' : sev === 'optimal' ? '#10b981' : '#3b82f6',
        bg: sev === 'critical' ? '#fee2e2' : sev === 'warning' ? '#fef3c7' : sev === 'optimal' ? '#d1fae5' : '#dbeafe',
        label: sev
      };
    };

    const evals = insight.thresholds_evaluation || {};
    const snapshot = insight.indicators_snapshot || {};

    console.log('[Indicators] === DIAGNOSTIC COMPLET ===');
    console.log('[Indicators] evals:', evals);
    console.log('[Indicators] snapshot:', snapshot);
    console.log('[Indicators] evals keys:', Object.keys(evals));
    console.log('[Indicators] snapshot keys:', Object.keys(snapshot));
    console.log('[Indicators] snapshot.dli:', snapshot.dli);
    console.log('[Indicators] dli.value:', snapshot.dli?.value);
    console.log('[Indicators] === STRUCTURE DÉTAILLÉE DLI ===');
    console.log('[Indicators] evals.dli COMPLET:', JSON.stringify(evals.dli, null, 2));
    console.log('[Indicators] evals.dli.threshold:', evals.dli?.threshold);
    console.log('[Indicators] evals.dli.threshold?.optimal:', evals.dli?.threshold?.optimal);

    // Helper pour reconstruire threshold.optimal depuis optimal_min/max
    const getOptimalRange = (evaluation) => {
      // Cas 1: threshold.optimal existe déjà (format "12-18 mol/m²/jour")
      if (evaluation?.threshold?.optimal) {
        return evaluation.threshold.optimal;
      }

      // Cas 2: Reconstruire depuis threshold.optimal_min/max
      const threshold = evaluation?.threshold;
      if (threshold?.optimal_min !== undefined && threshold?.optimal_max !== undefined) {
        return `${threshold.optimal_min}-${threshold.optimal_max} ${threshold.unit || evaluation?.unit || ''}`;
      }

      // Cas 3: Reconstruire depuis optimal_min/max au niveau racine (structure actuelle en base)
      if (evaluation?.optimal_min !== undefined && evaluation?.optimal_max !== undefined) {
        return `${evaluation.optimal_min}-${evaluation.optimal_max} ${evaluation?.unit || ''}`;
      }

      return null;
    };

    // 11 indicateurs calculés sur 24h
    const items = [
      {
        key: 'dli',
        title: 'DLI (Intégrale lumineuse journalière)',
        value: snapshot.dli?.value,
        unit: 'mol/m²/jour',
        icon: <Sun className="h-5 w-5" />,
        sev: evals.dli?.severity,
        optimal: getOptimalRange(evals.dli)
      },
      {
        key: 'sunlight_duration',
        title: "Durée d'ensoleillement",
        value: snapshot.sunlight_duration?.value,
        unit: 'h',
        icon: <Calendar className="h-5 w-5" />,
        sev: evals.sunlight_duration?.severity,
        optimal: getOptimalRange(evals.sunlight_duration)
      },
      {
        key: 'radiation_mean_diurnal',
        title: 'Radiation moyenne diurne (6h-20h)',
        value: snapshot.radiation_mean_diurnal?.value,
        unit: 'µmol/m²/s',
        icon: <Sun className="h-5 w-5" />,
        sev: evals.radiation_mean_diurnal?.severity,
        optimal: getOptimalRange(evals.radiation_mean_diurnal)
      },
      {
        key: 'vpd_diurnal',
        title: 'VPD diurne (moyenne)',
        value: snapshot.vpd_diurnal?.value,
        unit: 'kPa',
        icon: <Wind className="h-5 w-5" />,
        sev: evals.vpd_diurnal?.severity,
        optimal: getOptimalRange(evals.vpd_diurnal)
      },
      {
        key: 'soil_moisture',
        title: 'Humidité du substrat (moyenne 24h)',
        value: snapshot.soil_moisture?.value,
        unit: '%',
        icon: <Droplets className="h-5 w-5" />,
        sev: evals.soil_moisture?.severity,
        optimal: getOptimalRange(evals.soil_moisture)
      },
      {
        key: 'humidity',
        title: 'Humidité relative moyenne',
        value: snapshot.humidity?.value,
        unit: '%',
        icon: <Droplets className="h-5 w-5" />,
        sev: evals.humidity?.severity,
        optimal: getOptimalRange(evals.humidity)
      },
      {
        key: 'temperature_heat_stress',
        title: 'Stress chaud (>30°C)',
        value: snapshot.temperature_heat_stress?.value,
        unit: 'heures',
        icon: <Thermometer className="h-5 w-5" />,
        sev: evals.temperature_heat_stress?.severity,
        optimal: getOptimalRange(evals.temperature_heat_stress)
      },
      {
        key: 'temperature_cold_stress',
        title: 'Stress froid (<10°C)',
        value: snapshot.temperature_cold_stress?.value,
        unit: 'heures',
        icon: <Thermometer className="h-5 w-5" />,
        sev: evals.temperature_cold_stress?.severity,
        optimal: getOptimalRange(evals.temperature_cold_stress)
      },
      {
        key: 'temperature_amplitude',
        title: 'Amplitude thermique jour/nuit',
        value: snapshot.temperature_amplitude?.value,
        unit: '°C',
        icon: <Thermometer className="h-5 w-5" />,
        sev: evals.temperature_amplitude?.severity,
        optimal: getOptimalRange(evals.temperature_amplitude)
      },
      {
        key: 'co2_diurnal',
        title: 'CO2 diurne (moyenne)',
        value: snapshot.co2_diurnal?.value,
        unit: 'ppm',
        icon: <Wind className="h-5 w-5" />,
        sev: evals.co2_diurnal?.severity,
        optimal: getOptimalRange(evals.co2_diurnal)
      },
      {
        key: 'co2_saturation',
        title: 'Saturation CO2 24h (>1400 ppm)',
        value: snapshot.co2_saturation?.value,
        unit: 'heures',
        icon: <Wind className="h-5 w-5" />,
        sev: evals.co2_saturation?.severity,
        optimal: getOptimalRange(evals.co2_saturation)
      }
    ];

    // Debug: Vérifier chaque indicateur avant filtrage
    console.log('[DEBUG] Tous les items avant filtrage:');
    items.forEach(it => {
      console.log(`  - ${it.key}: value=${it.value}, title="${it.title}"`);
    });
    console.log('[DEBUG] snapshot.humidity:', snapshot.humidity);
    console.log('[DEBUG] snapshot.soil_moisture:', snapshot.soil_moisture);

    // Filtrer les indicateurs avec valeur
    const validItems = items.filter(it => it.value != null && !Number.isNaN(it.value));

    console.log('[Indicators] Items total:', items.length);
    console.log('[Indicators] Valid items:', validItems.length);
    console.log('[Indicators] Valid items details:', validItems.map(it => ({ key: it.key, value: it.value, sev: it.sev })));

    // Debug: Voir quel indicateur a été filtré
    console.log('[DEBUG] Indicateur(s) filtré(s):');
    const filtered = items.filter(it => it.value == null || Number.isNaN(it.value));
    filtered.forEach(it => {
      console.log(`  - ${it.key}: value=${it.value} (${typeof it.value}), title="${it.title}"`);
    });

    if (validItems.length === 0) {
      console.log('[Indicators] Aucun indicateur valide, retour null');
      return null;
    }

    return (
      <Card className="mb-4">
        <CardContent>
          <div className="flex items-center gap-2 mb-3">
            <Sun className="h-5 w-5 text-yellow-600" />
            <Typography variant="subtitle1" className="font-semibold">
              Indicateurs 24h (Lumière, VPD, Température, Substrat, CO2)
            </Typography>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {validItems.map((it) => {
              const cfg = getConfig(it.sev || 'acceptable');
              return (
                <div
                  key={it.key}
                  className="p-3 rounded-lg"
                  style={{ background: cfg.bg, borderLeft: `4px solid ${cfg.color}` }}
                >
                  <div className="flex items-center gap-2 text-gray-700 font-medium mb-1">
                    {it.icon}
                    <span className="text-xs">{it.title}</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold" style={{ color: cfg.color }}>
                      {typeof it.value === 'number' ? it.value.toFixed(it.key === 'dli' ? 2 : 1) : it.value}
                      <span className="ml-1 text-sm opacity-80" style={{ color: cfg.color }}>
                        {it.unit}
                      </span>
                    </div>
                    <span
                      style={{
                        color: cfg.color,
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase'
                      }}
                    >
                      {cfg.label}
                    </span>
                  </div>
                  {/* Afficher plage optimale */}
                  {it.optimal && (
                    <Typography
                      variant="caption"
                      style={{
                        color: '#059669',
                        marginTop: '4px',
                        display: 'block',
                        fontWeight: 500
                      }}
                    >
                      Optimal: <strong>{it.optimal}</strong>
                    </Typography>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    );
  };

  /**
   * Rendu d'une section d'analyse avec ReactMarkdown
   * UTILISE LA MÊME LOGIQUE QUE FarmRoadInsightsPanel.renderAnalysisSection()
   */
  const renderAnalysisSection = (title, content, icon) => {
    if (!content || content.trim().length === 0) return null;

    // Nettoyer le contenu markdown pour assurer un bon formatage
    const cleanedContent = cleanMarkdownText(content);

    return (
      <Card className="mb-4">
        <CardContent>
          <div className="flex items-center gap-2 mb-3">
            {icon}
            <Typography variant="h6" className="font-semibold">
              {title}
            </Typography>
          </div>
          <div className="prose prose-sm max-w-none markdown-content">
            <ReactMarkdown>{cleanedContent}</ReactMarkdown>
          </div>
        </CardContent>
      </Card>
    );
  };

  /**
   * Rendu du contenu de l'analyse
   */
  const renderAnalysisContent = () => {
    return (
      <div className="space-y-4">
        {/* Indicateurs 24h */}
        {render24hIndicatorsSection()}

        {/* Diagnostic */}
        {renderAnalysisSection(
          '🔍 Diagnostic Global',
          insight.diagnostic,
          <span className="text-blue-600">🩺</span>
        )}

        {/* Analyse Détaillée */}
        {renderAnalysisSection(
          '📊 Analyse Détaillée',
          insight.detailed_analysis,
          <span className="text-purple-600">🔬</span>
        )}

        {/* Recommandations */}
        {renderAnalysisSection(
          '💡 Recommandations',
          insight.recommendations,
          <span className="text-yellow-600">💡</span>
        )}

        {/* Prédictions */}
        {renderAnalysisSection(
          '⚠️ Alertes et Prédictions',
          insight.predictions,
          <span className="text-orange-600">📈</span>
        )}

        {/* Références scientifiques */}
        {renderAnalysisSection(
          '📚 Références Scientifiques',
          insight.scientific_references,
          <span className="text-indigo-600">📖</span>
        )}
      </div>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          backgroundColor: '#f9fafb'
        }
      }}
    >
      <DialogContent sx={{ p: 0 }}>
        <style>{`
          .markdown-content h1 {
            font-size: 1.5rem;
            font-weight: 700;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
            color: #111827;
          }
          .markdown-content h2 {
            font-size: 1.25rem;
            font-weight: 700;
            margin-top: 1.5rem;
            margin-bottom: 0.75rem;
            padding-left: 1rem;
            padding-top: 0.5rem;
            padding-bottom: 0.5rem;
            border-left: 4px solid #3b82f6;
            background: linear-gradient(90deg, #dbeafe 0%, transparent 100%);
            color: #1e40af;
          }
          .markdown-content h3 {
            font-size: 1.125rem;
            font-weight: 600;
            margin-top: 1rem;
            margin-bottom: 0.5rem;
            color: #374151;
          }
          .markdown-content p {
            margin-bottom: 0.75rem;
            line-height: 1.625;
            color: #4b5563;
          }
          .markdown-content ul, .markdown-content ol {
            margin-left: 1.5rem;
            margin-bottom: 0.75rem;
          }
          .markdown-content li {
            margin-bottom: 0.375rem;
            line-height: 1.5;
          }
          .markdown-content strong {
            font-weight: 600;
            color: #111827;
          }
          .markdown-content em {
            font-style: italic;
            color: #4b5563;
          }
          .markdown-content code {
            background-color: #f3f4f6;
            padding: 0.125rem 0.25rem;
            border-radius: 0.25rem;
            font-size: 0.875rem;
            color: #1f2937;
          }
        `}</style>
        <div className="max-w-7xl mx-auto p-6">
          {renderHeader()}
          {renderAnalysisContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FarmRoadHistoricalViewModal;
